/**
 * The named sound bank behind `ctx.audio.play(name, opts)`.
 *
 * Each entry: { build(ac, dest, t0, rng, opts), bus, seconds, bake }.
 *  - `bus`    which mix bus it belongs to (weapons / world / ui / music)
 *  - `bake`   pre-render round-robin variants offline for cheap playback
 *  - `seconds` worst-case length, used to size the offline render
 *
 * An unknown name is a silent no-op — never a throw.
 */
import { HANDLING, UI_SOUNDS } from './Handling.js';
import { buildImpact, buildExplosion, buildPenetration, SURFACE_NAMES } from './Impacts.js';
import { buildFootstep, buildLanding, buildSlide } from './Footsteps.js';
import { buildCrack, buildWhizby, buildRicochet, buildDebris } from './Weapons.js';

const bank = {};

function reg(name, build, { bus = 'world', seconds = 0.6, bake = true, variants = 4 } = {}) {
  bank[name] = { name, build, bus, seconds, bake, variants };
}

/* ---- handling (weapons bus, close and dry) ---- */
const HANDLING_SECONDS = {
  mag_release: 0.14, mag_out: 0.36, mag_in: 0.32, mag_seat: 0.28,
  bolt_back: 0.32, bolt_release: 0.36, charging_handle: 0.62, selector: 0.09,
  dryfire: 0.14, sling: 0.42, cloth: 0.32, gear: 0.28,
  weapon_raise: 0.42, weapon_lower: 0.48, shell_drop: 1.05,
};
for (const [name, fn] of Object.entries(HANDLING)) {
  reg(name, fn, { bus: 'weapons', seconds: HANDLING_SECONDS[name] ?? 0.5, variants: 4 });
}

/* ---- ui (dry, unspatialised) ---- */
const UI_SECONDS = {
  ui_click: 0.12, ui_hover: 0.08, ui_confirm: 0.30, ui_deny: 0.22,
  hitmarker: 0.10, headshot: 0.18, kill_confirm: 0.40, damage_hit: 0.40,
};
for (const [name, fn] of Object.entries(UI_SOUNDS)) {
  reg(name, fn, { bus: 'ui', seconds: UI_SECONDS[name] ?? 0.3, variants: 3 });
}

/* ---- impacts, one entry per surface ---- */
for (const s of SURFACE_NAMES) {
  reg(`impact_${s}`, (ac, dest, t0, rng, o) => buildImpact(ac, dest, t0, rng, s, o), {
    bus: 'world', seconds: 1.1, variants: 5,
  });
}
reg('penetration', buildPenetration, { bus: 'world', seconds: 0.28, variants: 3 });
reg('ricochet', (ac, d, t, r, o) => buildRicochet(ac, d, t, r, o), { bus: 'world', seconds: 0.75, variants: 5 });
reg('debris', (ac, d, t, r, o) => buildDebris(ac, d, t, r, o), { bus: 'world', seconds: 0.95, variants: 4 });
reg('explosion', buildExplosion, { bus: 'world', seconds: 2.6, variants: 3 });

/* ---- ballistics near the listener ---- */
reg('crack', buildCrack, { bus: 'weapons', seconds: 0.14, variants: 5 });
reg('whizby', buildWhizby, { bus: 'weapons', seconds: 0.30, variants: 4 });

/* ---- footsteps, one entry per surface ---- */
for (const s of SURFACE_NAMES) {
  reg(`step_${s}`, (ac, dest, t0, rng, o) => buildFootstep(ac, dest, t0, rng, s, o), {
    bus: 'world', seconds: 0.55, bake: false, variants: 0,
  });
  reg(`land_${s}`, (ac, dest, t0, rng, o) => buildLanding(ac, dest, t0, rng, s, o), {
    bus: 'world', seconds: 0.8, bake: false, variants: 0,
  });
}
reg('land', (ac, dest, t0, rng, o) => buildLanding(ac, dest, t0, rng, o?.surface || 'concrete', o), {
  bus: 'world', seconds: 0.8, bake: false,
});
reg('slide', (ac, dest, t0, rng, o) => buildSlide(ac, dest, t0, rng, o?.surface || 'concrete', o), {
  bus: 'world', seconds: 1.2, bake: false,
});

export const BANK = bank;
export function getSound(name) {
  return bank[name] || null;
}
export function bankNames() {
  return Object.keys(bank);
}
